import { useState } from "react";
import { Search, Loader2, Check, Info } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/**
 * Searches the web (Wikimedia Commons + the brand's own site icon) for a
 * partner or brand logo and lets the editor pick one.
 */
export default function LogoPicker({ brand = "", onPick, testKey }) {
  const [query, setQuery] = useState(brand);
  const [domain, setDomain] = useState("");
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  const search = async () => {
    if (!query.trim()) {
      toast.error("Type the brand or partner name first.");
      return;
    }
    setBusy(true);
    setOptions(null);
    try {
      const { data } = await api.post("/admin/ai/logos", { brand: query, domain });
      setOptions(data.options);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reach the logo search.");
      setOptions([]);
    } finally {
      setBusy(false);
    }
  };

  const pick = async (opt) => {
    setChosen(opt.url);
    try {
      // keep our own copy so a partner redesigning their site cannot blank the page
      const { data } = await api.post("/admin/ai/logos/save", {
        url: opt.full_url || opt.url,
        name: query || "logo",
      });
      onPick(data.url);
      toast.success(
        data.stored ? "Logo saved to your media library — press Save to publish" : "Logo applied — press Save to publish",
      );
    } catch (e) {
      onPick(opt.full_url || opt.url);
      toast.success("Logo applied — press Save to publish");
    }
  };

  return (
    <div className="border border-border bg-background/40 p-4" style={{ borderRadius: "var(--radius)" }} data-testid={`logo-picker-${testKey}`}>
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-primary">
        <Search className="h-3.5 w-3.5" /> Find a brand logo online
      </p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
        <Input
          value={query}
          placeholder="Brand or partner name"
          data-testid={`logo-query-${testKey}`}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Input
          value={domain}
          placeholder="Their website (optional) e.g. trasteel.com"
          data-testid={`logo-domain-${testKey}`}
          onChange={(e) => setDomain(e.target.value)}
        />
        <button
          type="button"
          onClick={search}
          disabled={busy}
          data-testid={`logo-search-${testKey}`}
          className="btn-outline h-10 px-5 text-xs disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Search"}
        </button>
      </div>

      {options !== null && options.length === 0 && !busy && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid={`logo-empty-${testKey}`}>
          Nothing usable found. Try the exact legal name, add their website, or upload the file in the image
          field below.
        </p>
      )}

      {options && options.length > 0 && (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={`logo-results-${testKey}`}>
            {options.map((opt) => (
              <button
                key={opt.url}
                type="button"
                onClick={() => pick(opt)}
                data-testid={`logo-option-${testKey}-${options.indexOf(opt)}`}
                className={`group relative flex aspect-[4/3] items-center justify-center overflow-hidden border p-3 transition-colors ${
                  chosen === opt.url ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/60"
                }`}
                style={{ borderRadius: "var(--radius)" }}
                title={`${opt.label} — ${opt.source}`}
              >
                <img src={opt.url} alt={opt.label} className="max-h-full max-w-full object-contain" />
                {chosen === opt.url && (
                  <span className="absolute end-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 truncate bg-background/85 px-1.5 py-1 text-[9px] text-muted-foreground">
                  {opt.source}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 h-3 w-3 shrink-0" />
            These marks belong to their owners. Only publish a partner logo you have permission to use.
          </p>
        </>
      )}
    </div>
  );
}
